// Kunden & Anlagen (E3, S5; Mockup /crm/kunden): der gemeinsame
// Kundenstamm – jede Anlage der Firma einmal, auch eine, deren Besichtigung
// draußen noch läuft. Je Zeile Kunde, Objekt, Adresse, Anlage, die Kiste, in
// der sie steht, der Stand der jüngsten Besichtigung und offene Fragen; ein
// Klick öffnet die Akte. Darüber „Filtern“ – nur im Browser, über diese
// Liste; die echte Suche über alles bleibt der Platzhalter oben („Suchen –
// Stufe 2“).

import { useMemo, useState } from "react";
import type { KundenAntwort, KundenZeile } from "@happy-energy/kern";
import { Chip, Ikon, Spaeter, Tipp } from "@happy-energy/oberflaeche";
import { kistenName, produktlinieKurz } from "../akte/akte.ts";
import { kuerzel, objektArtName, standArt } from "../anzeige.ts";
import { StandChip } from "../bausteine.tsx";
import { Laedt, LadeFehler, useLaden } from "../laden.tsx";
import { geheZu, Seite } from "../rahmen.tsx";
import { kundenFiltern } from "./filtern.ts";
import "../formular.css";
import "./kunden.css";

function Stand({ z }: { z: KundenZeile }) {
  if (!z.aufnahme) {
    return (
      // Ein Wort für „draußen noch nicht fertig“ – überall „noch nicht gesendet“ (O73).
      <Chip ikon="kamera">noch nicht gesendet</Chip>
    );
  }
  return <StandChip stand={standArt({ stand: z.aufnahme.stand, hinweis: z.hinweis }, true)} />;
}

export function Kunden() {
  const { daten, fehler, neu } = useLaden<KundenAntwort>("/api/crm/kunden");
  const [filter, setFilter] = useState("");
  const alle = useMemo(() => daten?.anlagen ?? [], [daten]);
  const zeilen = useMemo(() => kundenFiltern(alle, filter), [alle, filter]);

  return (
    <Seite titel="Kunden & Anlagen" unter="Der gemeinsame Kundenstamm – jeder Kunde einmal, mit Objekt und Anlage">
      {fehler !== undefined && <LadeFehler fehler={fehler} nochmal={neu} />}
      {!daten && fehler === undefined && <Laedt />}
      {daten && alle.length === 0 && (
        <Tipp>Noch kein Kunde – er entsteht, sobald der Verkauf in der Doku-App eine Besichtigung beginnt.</Tipp>
      )}
      {alle.length > 0 && (
        <>
          <div className="kunden-filter">
            <label className="crm-feld kunden-filter-feld">
              Filtern
              <span className="kunden-filter-eingabe">
                <Ikon name="suche" groesse="k" />
                <input
                  className="crm-eingabe"
                  type="search"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Name, Ort, PLZ oder Straße"
                  autoComplete="off"
                />
              </span>
            </label>
            <span className="leise klein">
              {filter.trim() ? `${zeilen.length} von ${alle.length}` : alle.length === 1 ? "1 Anlage" : `${alle.length} Anlagen`}
            </span>
          </div>
          {zeilen.length === 0 ? (
            <Tipp art="grau" ikon="suche">
              Kein Kunde passt zu „{filter.trim()}“.{" "}
              <button type="button" className="crm-knopf leise klein" onClick={() => setFilter("")}>
                Filter leeren
              </button>
            </Tipp>
          ) : (
            <div className="tabelle-wrap">
              <table className="tabelle">
                <thead>
                  <tr>
                    <th>Kunde</th>
                    <th>Objekt</th>
                    <th>Adresse</th>
                    <th>Anlage</th>
                    <th>Kiste</th>
                    <th>Stand</th>
                    <th>Fragen</th>
                  </tr>
                </thead>
                <tbody>
                  {zeilen.map((z) => (
                    <tr key={z.anlageId} className={`klickbar${z.hinweis ? ` hinweis-${z.hinweis}` : ""}`} onClick={() => geheZu(`/anlage/${z.anlageId}`)}>
                      <td>
                        <div className="tabelle-kunde">
                          <span className="avatar">{kuerzel(z.kunde.name)}</span>
                          <a href={`#/anlage/${z.anlageId}`} onClick={(e) => e.stopPropagation()}>
                            {z.kunde.name}
                          </a>
                        </div>
                      </td>
                      <td>{objektArtName(z.objekt.art)}</td>
                      <td>
                        {z.objekt.strasse}
                        <span className="tabelle-unter">
                          {z.objekt.plz} {z.objekt.ort}
                        </span>
                      </td>
                      <td>
                        <Chip ikon="sonne">{produktlinieKurz(z.produktlinie)}</Chip>
                      </td>
                      <td className="nicht-umbrechen">{kistenName(z.kiste)}</td>
                      <td>
                        <Stand z={z} />
                      </td>
                      <td>
                        {z.offeneFragen > 0 ? (
                          <Chip art="frage" ikon="frage">
                            {z.offeneFragen} offen
                          </Chip>
                        ) : (
                          <span className="leise">–</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      <Spaeter titel="Suchen, filtern, exportieren" wann="Stufe 2">
        Nach Name, Ort, Kiste oder Stand über alles suchen – und Unterlagen auf Wunsch zusätzlich in euren SharePoint ablegen.
      </Spaeter>
    </Seite>
  );
}
